/**
 * One-click community asks — the URL contract emails and links build against.
 *
 *   /ask/{type}/surprise-me          composer, prefilled, on a random recent piece
 *   /ask/{type}/{imageId}            composer, prefilled, on that piece
 *   /ask/{type}/surprise-me/post     prefilled confirm screen, one click to publish
 *   /ask/{type}/{imageId}/post       prefilled confirm screen, one click to publish
 *   /ask/{type}/choose               the artist picks the piece first, then the rest
 *                                    is filled in the same way (no /post form)
 *
 * `which-is-better` compares two pieces, so its target is `{idA},{idB}`; `surprise-me`
 * picks two and `choose` asks for two.
 * `surprise-me` in the image slot mirrors Art Review, Art Book and the Copilot, so one
 * convention covers every app an email can link into.
 */
export const ASK_TYPES = ['rating', 'pricing', 'critique', 'which-is-better'] as const;

export type AskType = (typeof ASK_TYPES)[number];

export const ASK_SURPRISE = 'surprise-me';

export const ASK_POST_SEGMENT = 'post';

/** The artist picks the piece(s) before the post is drafted. */
export const ASK_CHOOSE = 'choose';

/** Every ask posts into the General community, so a link never depends on memberships. */
export const ASK_COMMUNITY_SLUG = 'general';

/** Query param the post page reads to show the "feedback is on its way" confirmation. */
export const ASK_POSTED_PARAM = 'asked';

/** Query params the community feed reads to open the prefilled composer. */
export const ASK_PARAM = 'ask';
export const ASK_IMAGES_PARAM = 'images';

export interface AskSpec {
  type: AskType;
  /** Empty means surprise me (or the artist chooses, when `choose` is set). */
  imageIds: string[];
  post: boolean;
  choose: boolean;
}

export function imageCount(type: AskType): number {
  if (type === 'which-is-better') {
    return 2;
  }
  return 1;
}

export function isAskType(value: unknown): value is AskType {
  return ASK_TYPES.includes(value as AskType);
}

/** The ids in a target segment, or [] for surprise-me. Null when the segment is malformed. */
export function parseTarget(type: AskType, target: string): string[] | null {
  if (target === ASK_SURPRISE) {
    return [];
  }
  const ids = target.split(',').map((id) => id.trim());
  if (ids.length !== imageCount(type) || ids.some((id) => !/^[1-9]\d*$/.test(id))) {
    return null;
  }
  if (new Set(ids).size !== ids.length) {
    return null;
  }
  return ids;
}

/** `/ask/...` path segments after the locale, or null when they are not an ask. */
export function parseAskSpec(segments: string[]): AskSpec | null {
  const [type, target, mode, ...rest] = segments;
  if (!isAskType(type) || !target || rest.length > 0) {
    return null;
  }
  if (mode !== undefined && mode !== ASK_POST_SEGMENT) {
    return null;
  }
  const post = mode === ASK_POST_SEGMENT;
  if (target === ASK_CHOOSE) {
    if (post) {
      return null;
    }
    return { type, imageIds: [], post: false, choose: true };
  }
  const imageIds = parseTarget(type, target);
  if (!imageIds) {
    return null;
  }
  return { type, imageIds, post, choose: false };
}

/** The `{target}` segment: `choose`, `surprise-me`, or the ids. */
export function targetSegment(imageIds: string[], choose = false): string {
  if (choose) {
    return ASK_CHOOSE;
  }
  if (imageIds.length === 0) {
    return ASK_SURPRISE;
  }
  return imageIds.join(',');
}

/** The path for an ask link. Pass no ids for surprise me. */
export function askPath(type: AskType, imageIds: string[] = [], { post = false, choose = false } = {}): string {
  const target = targetSegment(imageIds, choose);
  const base = `/ask/${type}/${target}`;
  if (!post) {
    return base;
  }
  return `${base}/${ASK_POST_SEGMENT}`;
}
